/**
 * OfflineBanner — Slim-Banner top, sichtbar wenn der Browser offline ist.
 *
 * Spec: docs/design-spec.md §7 (Offline) + §8 (Mobile/PWA)
 *  - Slim-Banner top in Amber
 *  - Dismissible (per Session — nach Reload sichtbar bei Bedarf)
 *  - aria-live="polite" (Status-Information, nicht assertive)
 *
 * Implementierung:
 *   - navigator.onLine + window.online/offline-Events
 *   - SSR-safe (initial true, falls window undefined)
 *   - Beim Wieder-online ein kurzer "Wieder verbunden"-Toast (3s),
 *     danach Banner ausblenden.
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, WifiOff, X } from "lucide-react";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { cn } from "@/lib/utils";

type Mode = "offline" | "reconnected" | "hidden";

const RECONNECT_TOAST_MS = 2500;

function getInitialOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
}

export function OfflineBanner() {
  const [online, setOnline] = useState<boolean>(getInitialOnline);
  const [mode, setMode] = useState<Mode>(() => (getInitialOnline() ? "hidden" : "offline"));
  const [dismissed, setDismissed] = useState(false);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    function onOnline() {
      setOnline(true);
    }
    function onOffline() {
      setOnline(false);
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // Mode-Maschine reagiert auf online-Wechsel
  useEffect(() => {
    if (!online) {
      setMode("offline");
      setDismissed(false);
      return;
    }
    // online geworden — falls vorher offline-Banner sichtbar war,
    // kurz "Wieder verbunden" anzeigen.
    if (mode === "offline") {
      setMode("reconnected");
      const t = setTimeout(() => setMode("hidden"), RECONNECT_TOAST_MS);
      return () => clearTimeout(t);
    }
    // sonst: schon hidden, nichts zu tun
    return;
  }, [online, mode]);

  const visible = !dismissed && (mode === "offline" || mode === "reconnected");
  const isOffline = mode === "offline";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          initial={reducedMotion ? false : { y: -32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reducedMotion ? { opacity: 0 } : { y: -32, opacity: 0 }}
          transition={{
            duration: reducedMotion ? 0 : 0.18,
            ease: [0.16, 1, 0.3, 1],
          }}
          className={cn(
            "fixed left-0 right-0 top-0 z-[60]",
            "flex items-center justify-center gap-2 px-3 py-1.5",
            "text-tiny font-medium",
            isOffline
              ? "bg-status-pending-bg text-status-pending-text"
              : "bg-status-approved-bg text-status-approved-text",
          )}
          style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 0.375rem)" }}
        >
          <span className="flex items-center gap-2">
            {isOffline ? (
              <>
                <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
                <span>
                  Du bist offline. Änderungen werden gespeichert und synchronisiert,
                  sobald die Verbindung zurück ist.
                </span>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Wieder verbunden.</span>
              </>
            )}
          </span>
          {isOffline && (
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Offline-Hinweis ausblenden"
              className={cn(
                "ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full",
                "transition-colors hover:bg-black/10",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current",
              )}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
